import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { usePlayerStore } from '../store/playerStore';
import { useUniverseStore } from '../store/universeStore';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { getPlayerState } from '../services/science.service';
import { destroyBuilding, type PlanetActionOptions } from '../services/planet.service';
import { purchaseEmergencyOzone } from '../services/premium.service';
import { getBuildingIcon } from '../utils/buildingIcons';
import { formatFullNumber } from '../utils/formatters';
import ResourceQuantities from './ResourceQuantities';
import { Icon } from './ui/Icon';
import { Panel } from './ui/Panel';
import { Button } from './ui/Button';
import { IconButton } from './ui/IconButton';
import { Chip } from './ui/Chip';
import { zoneAccentStyle } from './planet/zoneAccent';
import {
    BUILDING_DEFINITIONS,
    OZONE_MAX,
    RESOURCE_NAMES,
    EMERGENCY_OZONE_PRICE,
    type BuildingType,
    type Planet,
    type PlayerRace,
    type ResourceCost,
} from '@kaeliux/shared';
import {
    calculateBuildingUpgradeCost,
    calculateNominalRefundAtRates,
    calculateOzoneChange,
    estimateOzoneUnlockDays,
    REFUND_RATES,
} from '@kaeliux/game-engine';

function formatEtaDays(days: number): string {
    if (days < 1) {
        const hours = Math.max(1, Math.round(days * 24));
        return `${hours} hour${hours === 1 ? '' : 's'}`;
    }
    return `${days.toFixed(1)} days`;
}

function sumRefund(a: ResourceCost, b: ResourceCost): ResourceCost {
    return {
        metal: (a.metal ?? 0) + (b.metal ?? 0),
        minerals: (a.minerals ?? 0) + (b.minerals ?? 0),
        gas: (a.gas ?? 0) + (b.gas ?? 0),
    };
}

/**
 * Nominal refund for destroying `count` levels off the top of `buildingType`
 * (currently at `level`) — mirrors what the server actually credits one
 * `destroyBuilding` call at a time (see `destroyBuildingLevel` in
 * `tickEvents.ts`): each successive level is priced at
 * `architectureLevel = 0`, irrespective of the player's real discount.
 */
function refundForLevels(buildingType: BuildingType, level: number, count: number): ResourceCost {
    if (level <= 0) return {};
    let total: ResourceCost = {};
    for (let i = 0; i < count; i++) {
        const fromLevel = level - i;
        const cost = calculateBuildingUpgradeCost(buildingType, fromLevel - 1, 0);
        total = sumRefund(total, calculateNominalRefundAtRates(cost, REFUND_RATES.building, 1));
    }
    return total;
}

interface DestroyBuildingsPanelProps {
    planet: Planet;
    race: PlayerRace | undefined;
    options: PlanetActionOptions;
    onCancel: () => void;
    /** Sync the (possibly partially-updated) planet back up without leaving the panel. */
    onPlanetUpdated: (planet: Planet) => void;
    /** The whole batch finished — safe to leave the destroy view. */
    onDestroyed: () => void;
}

/**
 * Building destruction list shown from the lock overlay. Reuses the same
 * `destroyBuilding` endpoint the (unlocked) ZonePage building cards use, one
 * level at a time — batch counts here just drive N sequential calls, since
 * the server is the source of truth for each level's actual refund.
 *
 * Rows/levels/refunds all read `planet` live and undisturbed: no client-side
 * snapshot, so they can never disagree with each other mid-render. Drift
 * across a failed batch is instead avoided by `handleConfirm` itself —
 * whatever levels actually got destroyed before an error get pushed back up
 * via `onPlanetUpdated`, and `counts` is cleared so stale selections can't be
 * re-confirmed against building levels that no longer exist.
 */
function DestroyBuildingsPanel({ planet, race, options, onCancel, onPlanetUpdated, onDestroyed }: DestroyBuildingsPanelProps) {
    const rows = (Object.values(BUILDING_DEFINITIONS) as (typeof BUILDING_DEFINITIONS)[BuildingType][])
        .filter((def) => (planet.buildings[def.id]?.level ?? 0) > 0);

    const [counts, setCounts] = useState<Partial<Record<BuildingType, number>>>({});
    const [isDestroying, setIsDestroying] = useState(false);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const [error, setError] = useState<string | null>(null);

    function adjust(buildingType: BuildingType, level: number, delta: number) {
        setCounts((prev) => {
            const next = Math.max(0, Math.min(level, (prev[buildingType] ?? 0) + delta));
            return { ...prev, [buildingType]: next };
        });
    }

    const entries = (Object.entries(counts) as [BuildingType, number][]).filter(([, n]) => n > 0);
    const totalLevels = entries.reduce((sum, [, n]) => sum + n, 0);
    const totalRefund = entries.reduce((total, [buildingType, n]) => {
        const level = planet.buildings[buildingType]?.level ?? 0;
        return sumRefund(total, refundForLevels(buildingType, level, n));
    }, {} as ResourceCost);

    async function handleConfirm() {
        setIsDestroying(true);
        setError(null);
        let done = 0;
        let lastPlanet = planet;
        let succeeded = false;
        setProgress({ done: 0, total: totalLevels });
        try {
            for (const [buildingType, count] of entries) {
                for (let i = 0; i < count; i++) {
                    lastPlanet = await destroyBuilding(planet.id, buildingType, options);
                    done += 1;
                    setProgress({ done, total: totalLevels });
                }
            }
            succeeded = true;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Destruction failed');
        } finally {
            if (done > 0) {
                onPlanetUpdated(lastPlanet);
                setCounts({});
            }
            setIsDestroying(false);
            setProgress(null);
        }
        if (succeeded) onDestroyed();
    }

    return (
        <div className="w-full flex justify-center px-4 py-8">
            <Panel variant="solid" padding="lg" className="w-full max-w-3xl flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-heading text-content-primary">Destroy buildings</h2>
                    <Button variant="secondary" size="sm" onClick={onCancel} disabled={isDestroying}>
                        ← Back
                    </Button>
                </div>
                <p className="text-content-muted text-body">
                    Choose how many levels of each building to destroy. Destroyed levels refund ~70% of the
                    resources spent to build them and stop reducing your {RESOURCE_NAMES.ozone} supply.
                </p>

                {rows.length === 0 ? (
                    <p className="text-content-muted text-body">This planet has no buildings to destroy.</p>
                ) : (
                    <div className="flex flex-col gap-2">
                        {rows.map((def) => {
                            const level = planet.buildings[def.id]?.level ?? 0;
                            const count = counts[def.id] ?? 0;
                            return (
                                <Panel key={def.id} variant="solid" padding="sm" className="flex items-center gap-3">
                                    <img
                                        src={getBuildingIcon(def.id, level, race)}
                                        alt=""
                                        className="w-12 aspect-[3/4] object-cover rounded-tile bg-space-950 border border-space-700 shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-content-primary font-semibold">{def.name}</div>
                                        <div className="text-content-muted text-micro">Level {level}</div>
                                        {count > 0 && (
                                            <div className="text-content-muted text-micro mt-1">
                                                Refund: <ResourceQuantities amounts={refundForLevels(def.id, level, count)} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <IconButton
                                            variant="secondary"
                                            size="lg"
                                            onClick={() => adjust(def.id, level, -1)}
                                            disabled={count <= 0 || isDestroying}
                                            aria-label={`Destroy one fewer level of ${def.name}`}
                                        >
                                            −
                                        </IconButton>
                                        <span className="w-6 text-center text-content-primary">{count}</span>
                                        <IconButton
                                            variant="secondary"
                                            size="lg"
                                            onClick={() => adjust(def.id, level, 1)}
                                            disabled={count >= level || isDestroying}
                                            aria-label={`Destroy one more level of ${def.name}`}
                                        >
                                            +
                                        </IconButton>
                                    </div>
                                </Panel>
                            );
                        })}
                    </div>
                )}

                {totalLevels > 0 && !isDestroying && (
                    <Panel variant="solid" padding="sm" className="flex justify-between">
                        <span className="text-content-muted text-body">Total refund ({totalLevels} level{totalLevels === 1 ? '' : 's'})</span>
                        <span className="text-content-primary font-semibold text-body"><ResourceQuantities amounts={totalRefund} /></span>
                    </Panel>
                )}

                {error && <p className="text-status-danger text-body">{error}</p>}

                <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={onCancel} disabled={isDestroying}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirm}
                        disabled={totalLevels === 0 || isDestroying}
                    >
                        {isDestroying
                            ? `Destroying… (${progress?.done ?? 0}/${progress?.total ?? totalLevels})`
                            : `Destroy ${totalLevels} level${totalLevels === 1 ? '' : 's'}`}
                    </Button>
                </div>
            </Panel>
        </div>
    );
}

interface PlanetLockOverlayProps {
    planet: Planet;
    onPlanetUpdated: (planet: Planet) => void;
}

/**
 * Replaces the routed content (in place of `<Outlet />`) when the player's
 * planet is ozone-locked (`docs/game-details/planet-lock-mechanism.md`,
 * issue #11) — every planet-dependent route hits this, not just the planet
 * page. `AppShell` keeps the nav chrome around it (a future planet
 * switcher lives there, and /personal is exempted since it doesn't depend on
 * any one planet), so this renders as normal in-flow page content rather
 * than a full-screen takeover.
 */
export default function PlanetLockOverlay({ planet, onPlanetUpdated }: PlanetLockOverlayProps) {
    const { profile } = useAuthStore();
    const { resourceMultiplier, sunAges, speedMultiplier } = useUniverseStore();
    const { openDialog } = useConfirmDialog();
    const navigate = useNavigate();
    const darkMatter = usePlayerStore((s) => s.premium?.darkMatter ?? 0);
    const [view, setView] = useState<'lock' | 'destroy'>('lock');
    const [ecologyLevel, setEcologyLevel] = useState(0);
    const [purchaseError, setPurchaseError] = useState<string | null>(null);

    const options: PlanetActionOptions = useMemo(
        () => ({ resourceMultiplier, sunAges, speedMultiplier }),
        [resourceMultiplier, sunAges, speedMultiplier],
    );

    useEffect(() => {
        if (!profile?.id) return;
        let cancelled = false;
        getPlayerState(profile.id, planet.id)
            .then((state) => {
                if (cancelled) return;
                setEcologyLevel(state.sciences.ecology ?? 0);
                // getPlayerState resolves any due research by ticking the
                // planet as a side effect (see science.service.ts) and saves
                // the result, so its `planet` is newer than the one this
                // overlay was rendered with — e.g. an Ozone recovery that just
                // crossed the unlock threshold. Adopt it, or the overlay goes
                // stale relative to what was just saved.
                if (state.planet) onPlanetUpdated(state.planet);
            })
            .catch((err) => console.error('Failed to load Ecology level:', err));
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile?.id, planet.id]);

    const netChangePerDay = calculateOzoneChange(planet.buildings, ecologyLevel, 86_400);
    const etaDays = estimateOzoneUnlockDays(planet.state.ozone.current, netChangePerDay);

    function showComingSoon(title: string, message: string) {
        openDialog({ title, message, confirmLabel: 'Got it', cancelLabel: 'Close', onConfirm: () => { } });
    }

    async function completeEmergencyOzonePurchase() {
        setPurchaseError(null);
        try {
            const { planet: updated, premium } = await purchaseEmergencyOzone(planet.id);
            usePlayerStore.setState({ premium });
            onPlanetUpdated(updated);
        } catch (e) {
            setPurchaseError((e as Error)?.message ?? `Could not buy Emergency ${RESOURCE_NAMES.ozone}`);
        }
    }

    function handleBuyEmergencyOzone() {
        setPurchaseError(null);
        if (darkMatter < EMERGENCY_OZONE_PRICE) {
            openDialog({
                title: 'Not enough Dark Matter',
                message: `Emergency ${RESOURCE_NAMES.ozone} costs ${EMERGENCY_OZONE_PRICE} Dark Matter — you have ${formatFullNumber(darkMatter)}. Get more from the Premium Store.`,
                confirmLabel: 'Open Store',
                cancelLabel: 'Close',
                onConfirm: () => navigate('/premium'),
            });
            return;
        }
        openDialog({
            title: `Buy Emergency ${RESOURCE_NAMES.ozone}`,
            message: `Instantly restore this planet to full ${RESOURCE_NAMES.ozone} supply for ${EMERGENCY_OZONE_PRICE} Dark Matter? Your balance is ${formatFullNumber(darkMatter)}.`,
            confirmLabel: 'Buy',
            cancelLabel: 'Cancel',
            onConfirm: () => { void completeEmergencyOzonePurchase(); },
        });
    }

    if (view === 'destroy') {
        return (
            <DestroyBuildingsPanel
                key={planet.id}
                planet={planet}
                race={profile?.race}
                options={options}
                onCancel={() => setView('lock')}
                onPlanetUpdated={onPlanetUpdated}
                onDestroyed={() => setView('lock')}
            />
        );
    }

    return (
        <div className="w-full flex justify-center px-4 py-8">
            <Panel
                variant="accent"
                padding="lg"
                style={zoneAccentStyle('military')}
                className="w-full max-w-2xl flex flex-col gap-6"
            >
                <div className="flex flex-col gap-2 text-center">
                    <Icon name="alert" size={36} className="text-status-danger mx-auto" aria-hidden="true" />
                    <h1 className="text-heading text-status-danger">{`Your planet's ${RESOURCE_NAMES.ozone} supply has reached a critical level.`}</h1>
                </div>

                <p className="text-content-secondary text-body text-center">
                    All personnel have been evacuated for safety, hence your production has been frozen. Your ships
                    will remain untouched but since there&rsquo;s nobody to fly them away, you cannot control them.
                </p>
                <p className="text-content-secondary text-body text-center">
                    You must take immediate action to restore this planet&rsquo;s {RESOURCE_NAMES.ozone} supply. You have three
                    options:
                </p>

                <div className="grid gap-3 sm:grid-cols-3">
                    <Panel variant="solid" padding="md" className="flex flex-col gap-2 text-left">
                        <Icon name="science" size={22} aria-hidden="true" />
                        <h3 className="font-semibold text-content-primary text-title">Upgrade Ecology</h3>
                        <p className="text-content-muted text-body">
                            Increases your {RESOURCE_NAMES.ozone} income across all your planets. Research it from any other
                            unlocked planet.
                        </p>
                    </Panel>
                    <button
                        type="button"
                        className="rounded-panel border border-space-700 bg-space-800 p-4 flex flex-col gap-2 text-left hover:border-space-500 transition-colors"
                        onClick={() => setView('destroy')}
                    >
                        <Icon name="destroy" size={22} aria-hidden="true" />
                        <h3 className="font-semibold text-content-primary text-title">Destroy buildings</h3>
                        <p className="text-content-muted text-body">
                            Reach a positive {RESOURCE_NAMES.ozone} balance and wait for it to recover above the critical level.
                        </p>
                    </button>
                    <button
                        type="button"
                        className="rounded-panel border border-space-700 bg-space-800 p-4 flex flex-col gap-2 text-left hover:border-space-500 transition-colors"
                        onClick={handleBuyEmergencyOzone}
                    >
                        <Icon name="capsule" size={22} aria-hidden="true" />
                        <h3 className="font-semibold text-content-primary text-title">{`Buy Emergency ${RESOURCE_NAMES.ozone}`}</h3>
                        <p className="text-content-muted text-body">
                            Instantly resolve your {RESOURCE_NAMES.ozone} issues from the galactic market for {EMERGENCY_OZONE_PRICE} Dark Matter.
                        </p>
                    </button>
                </div>

                {purchaseError && (
                    <p className="text-status-danger text-body text-center" role="alert">{purchaseError}</p>
                )}

                <Panel variant="solid" padding="md" className="flex flex-col gap-2">
                    <div className="flex justify-between">
                        <span className="text-content-muted">{RESOURCE_NAMES.ozone}</span>
                        <Chip tone="danger">
                            {formatFullNumber(planet.state.ozone.current)} / {formatFullNumber(OZONE_MAX)}
                        </Chip>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-content-muted">Daily change</span>
                        <span className={netChangePerDay >= 0 ? 'text-status-success font-semibold' : 'text-status-danger font-semibold'}>
                            {netChangePerDay >= 0 ? '+' : ''}{Math.round(netChangePerDay)} / day
                        </span>
                    </div>
                    <div className="text-content-muted text-body">
                        {etaDays !== null
                            ? `Estimated time to unlock: ${formatEtaDays(etaDays)}`
                            : netChangePerDay < 0
                                ? `${RESOURCE_NAMES.ozone} supply is still declining — upgrade Ecology or destroy buildings to reverse this.`
                                : `${RESOURCE_NAMES.ozone} supply isn't recovering yet — upgrade Ecology or destroy buildings to start reversing this.`}
                    </div>
                </Panel>

                <button
                    type="button"
                    className="self-center text-status-danger hover:brightness-110 text-body underline underline-offset-2"
                    onClick={() => showComingSoon('Delete this planet', "Planet deletion isn't available yet — check back soon.")}
                >
                    Delete this planet
                </button>
            </Panel>
        </div>
    );
}
